import { RouteLocationNormalized, Router } from 'vue-router';

import store from '@/store/store';
import { parseJwt } from '@/bundles/App/helpers/parseJWT';
import { RoleNameEnum } from '@/bundles/Roles/types/enums';
import { AnalyticsEventEnum, DataLayerParams, IAnalyticsIdentityState, KeyActionEnum } from '@/bundles/Analytics/types';

const GTM_ID = process.env.VUE_APP_GTM_ID;

const NOT_FOUND_PAGE = 'not-found';

// GA4 drops event parameter values longer than this
export const MAX_VALUE_LENGTH = 100;

// Tells GTM that this build reports key events on API success (sc_action_success), so its
// click-based key event trigger, kept for builds still open in old tabs, stays quiet.
const KEY_EVENT_SOURCE = 'success';

// The first role in this list that the user holds is reported as user_role.
const ROLE_PRIORITY: string[] = [
  RoleNameEnum.SUPER_ADMIN,
  RoleNameEnum.ADMIN,
  RoleNameEnum.FINANCIAL_ADMIN,
  RoleNameEnum.HR_ADMIN,
  RoleNameEnum.MARKETING_ADMIN,
  RoleNameEnum.CLIENT,
];

// The session recorder (plugins/sessionRecorder) files each replay under this id, so a GA4 row
// leads to its recording at <recorder>/dashboard/#/session/<recording_session_id>.
function recordingSessionId (): string | undefined {
  return window.SessionRecorder?.getSessionUrl()?.split('/session/')[1] || undefined;
}

export function track (event: AnalyticsEventEnum, params: DataLayerParams = {}): void {
  (window.dataLayer = window.dataLayer || []).push({ event, recording_session_id: recordingSessionId(), ...params });
}

// Route params (account ids, record ids, hashes, campaign contacts) never leave the app:
// a page is reported by its route template, e.g. /:accountId/property/:id.
function routeTemplate (route: Pick<RouteLocationNormalized, 'matched'>): string | undefined {
  const record = route.matched[route.matched.length - 1];

  // the catch-all (`/:pathMatch(.*)*`) is the not-found page
  if (!record || record.path.endsWith('*')) return undefined;

  return record.path || '/';
}

function pageGroup (template: string): string {
  const [section] = template.replace(/^\/:accountId/, '').split('/').filter(Boolean);

  return section || 'root';
}

function pageUrl (template: string | undefined): string {
  return `${ window.location.origin }${ template ?? `/${ NOT_FOUND_PAGE }` }`;
}

// The first page's referrer is either another page of the app, which gets the same
// template treatment, or an external site, which is reported by its origin only.
function initialReferrer (router: Router): string {
  if (!document.referrer) return '';

  const referrer = new URL(document.referrer);

  if (referrer.origin !== window.location.origin) return referrer.origin;

  return pageUrl(routeTemplate(router.resolve(referrer.pathname)));
}

function trackPageViews (router: Router): void {
  let referrer: string | undefined;

  router.afterEach((to, from, failure) => {
    // Router 4 also runs afterEach for a navigation a guard aborted: the user did not move
    if (failure) return;

    // query and hash changes (filters, tabs, pagination) are not new pages
    if (from.matched.length && to.path === from.path) return;

    const template = routeTemplate(to);
    const location = pageUrl(template);

    track(AnalyticsEventEnum.PageView, {
      page_location: location,
      page_referrer: referrer ?? initialReferrer(router),
      page_title: (to.meta?.title as string | undefined) ?? '',
      page_name: template ? String(to.name ?? template) : NOT_FOUND_PAGE,
      page_group: template ? pageGroup(template) : NOT_FOUND_PAGE,
    });

    referrer = location;
  });
}

function identity (): DataLayerParams {
  const { userId, activeAccount, idToken } = store.state as IAnalyticsIdentityState;

  if (!userId) {
    return { user_id: undefined, account_id: undefined, user_role: undefined };
  }

  // Roles are read from the session token, as App.vue does: it copies them into the store
  // only once it is created, which is after the first page view has been reported.
  const scope = idToken ? parseJwt(idToken)?.scope : undefined;
  const roles: string[] = Array.isArray(scope) ? scope : [];

  return {
    user_id: String(userId),
    account_id: activeAccount?.id ? String(activeAccount.id) : undefined,
    user_role: ROLE_PRIORITY.find((role) => roles.includes(role)) ?? 'user',
  };
}

function trackIdentity (): void {
  // no event: GTM keeps these keys, so every following tag reads the current values
  window.dataLayer.push(identity());

  store.watch(
    (state: IAnalyticsIdentityState) => [state.userId, state.activeAccount?.id, state.idToken].join('|'),
    () => window.dataLayer.push(identity()),
  );
}

// Only elements that opt in with data-track="domain.object.action" are reported, so a
// button's text (which can be a contact's or a company's name) is never read. Capture phase
// on the document runs before any handler on the way down, so @click.stop cannot hide a click.
function onClick (event: MouseEvent): void {
  const element = event.target instanceof Element ? event.target.closest('[data-track]') : null;

  if (!element) return;

  track(AnalyticsEventEnum.Click, trackIdParams(element.getAttribute('data-track') || ''));
}

function trackIdParams (trackId: string): DataLayerParams {
  const parts = trackId.split('.');

  return {
    track_id: trackId,
    track_domain: parts[0],
    track_object: parts.slice(1, -1).join('.'),
    track_action: parts.length > 1 ? parts[parts.length - 1] : '',
  };
}

// Called right after the API confirms a key action, so a click that ends in a validation or API
// error is not counted as a created record or a sent e-mail.
export function trackActionSuccess (action: KeyActionEnum): void {
  track(AnalyticsEventEnum.ActionSuccess, trackIdParams(action));
}

function loadGtm (): void {
  const script = document.createElement('script');

  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${ GTM_ID }`;
  document.head.appendChild(script);
}

export function initGtm (router: Router): void {
  window.dataLayer = window.dataLayer || [];

  // gtm.js goes first so GTM's initialization tags run before any app event in the queue
  if (GTM_ID) window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

  window.dataLayer.push({ key_event_source: KEY_EVENT_SOURCE });
  trackIdentity();
  trackPageViews(router);
  document.addEventListener('click', onClick, true);

  if (!GTM_ID) return;

  // Nothing on first paint needs GTM, so it waits for an idle moment; capped, because map
  // pages (mapbox + the vendor chunk) may not go idle for seconds. Queued events are kept.
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(loadGtm, { timeout: 3000 });
  } else {
    setTimeout(loadGtm, 0);
  }
}
